import React, { useState } from 'react';
import './GeoReportList.scss';
import { useTranslation } from 'react-i18next';
import { DataTable } from '../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { format } from 'date-fns';
import { GeoReportResponse } from '../../../../shared/models/geo-report/GeoReportResponse';
import { useGeoReportStatusPoll } from '../../../../shared/api/hooks/geo-report/useGeoReportStatusPoll';
import GeoReportPreview from '../geo-report-preview/GeoReportPreview';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import {
  MetadataBadge,
  MetadataBadgeVariant,
} from '../../../../shared/components/metadata-badge/MetadataBadge';

const STATUS_VARIANT: Record<GeoReportResponse['status'], MetadataBadgeVariant> = {
  PENDING: 'pending',
  GENERATING: 'generating',
  COMPLETED: 'success',
  FAILED: 'danger',
};

interface GeoReportStatusPollerProps {
  report: GeoReportResponse;
}

/**
 * Invisible component mounted per in-flight row to activate status polling.
 * Hooks cannot be called inside DataTable body template callbacks, so we mount
 * a tiny component per row that owns the poll hook call.
 */
const GeoReportStatusPoller: React.FC<GeoReportStatusPollerProps> = ({ report }) => {
  useGeoReportStatusPoll({
    id: report.id,
    status: report.status === 'PENDING' || report.status === 'GENERATING' ? report.status : null,
  });
  return null;
};

interface GeoReportListProps {
  reports: GeoReportResponse[];
}

const GeoReportList: React.FC<GeoReportListProps> = ({ reports }) => {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const urlBodyTemplate = (report: GeoReportResponse) => (
    <span className="geo-report-list__url" title={report.url}>
      {report.url.replace(/^https?:\/\//, '')}
    </span>
  );

  const statusBodyTemplate = (report: GeoReportResponse) => (
    <MetadataBadge
      variant={STATUS_VARIANT[report.status]}
      text={t(`geoReport.status.${report.status.toLowerCase()}`)}
      pulse={report.status === 'GENERATING'}
    />
  );

  const scoreBodyTemplate = (report: GeoReportResponse) => {
    if (report.geoScore !== null) {
      const tier = report.geoScore >= 70 ? 'high' : report.geoScore >= 40 ? 'mid' : 'low';
      return (
        <span className={`geo-report-list__score geo-report-list__score--${tier}`}>
          {report.geoScore}
        </span>
      );
    }
    return <span className="geo-report-list__score-empty">—</span>;
  };

  // Data i godzina osobno: na desktopie w jednej linii („2026-10-01 / 22:30”), w karcie na telefonie
  // godzina pod datą — razem nie mieściły się w kolumnie siatki.
  const dateBodyTemplate = (report: GeoReportResponse) => (
    <>
      <span>{format(new Date(report.createdAt), 'yyyy-MM-dd')}</span>
      <span className="geo-report-list__time">{format(new Date(report.createdAt), 'HH:mm')}</span>
    </>
  );

  const rowExpansionTemplate = (report: GeoReportResponse) => {
    // Note only when the account has actually seen THIS cached result before — tie it to the copy's
    // SOURCE crawl, not just the URL: the source is the account's own crawl (r.id === src) OR the
    // account has an earlier copy of that same source. (Matching by URL alone wrongly fired for a
    // cross-account copy when the account happened to have an old report of the same URL. ids serial.)
    const src = report.sourceReportId;
    const cachedFromOwnAudit =
      src != null && reports.some((r) => r.id < report.id && (r.id === src || r.sourceReportId === src));
    return (
      <div className="geo-report-list__preview-cell">
        <GeoReportPreview report={report} cachedFromOwnAudit={cachedFromOwnAudit} />
      </div>
    );
  };

  return (
    <div className="geo-report-list">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">{t('geoReport.reportsTitle')}</div>
      </div>
      {reports.map((report) => (
        <GeoReportStatusPoller key={report.id} report={report} />
      ))}
      <DataTable
        value={reports}
        dataKey="id"
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data as Record<number, boolean>)}
        rowExpansionTemplate={rowExpansionTemplate}
        tableClassName="table"
        className="table-wrapper table-wrapper--accordion table-wrapper--stack"
        responsiveLayout="stack"
        emptyMessage={() => <EmptyPanel textKey="geoReport.emptyState" />}
        rowClassName={(data: GeoReportResponse) => {
          const idx = reports.indexOf(data);
          return idx % 2 === 1 ? 'geo-report-list__row--even' : '';
        }}
      >
        <Column
          field="url"
          header={t('geoReport.colUrl')}
          bodyClassName="table__cell--stack-title"
          headerClassName="table__header"
          body={urlBodyTemplate}
          style={{ maxWidth: '320px' }}
        />
        <Column
          field="status"
          header={t('geoReport.colStatus')}
          headerClassName="table__header"
          body={statusBodyTemplate}
        />
        <Column
          field="geoScore"
          header={t('geoReport.colScore')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          body={scoreBodyTemplate}
          style={{ width: '80px' }}
        />
        <Column
          field="createdAt"
          header={t('geoReport.colDate')}
          headerClassName="table__header"
          bodyClassName="geo-report-list__cell--date"
          body={dateBodyTemplate}
          style={{ width: '130px' }}
        />
        <Column
          expander={(report: GeoReportResponse) => report.status === 'COMPLETED'}
          bodyClassName="geo-report-list__cell--expander table__cell--stack-actions"
          style={{ width: '40px' }}
        />
      </DataTable>
    </div>
  );
};

export { GeoReportList };
export default GeoReportList;
